/**
 * AP Invoices Demo — Nami chat-input interceptor.
 *
 * THROWAWAY DEMO CODE. `useApDemoInterceptor().tryIntercept(message)` is called
 * by the global chat renderer's send handler. When AP demo mode is on and the
 * message matches a scripted beat, it streams the narration (no backend call)
 * and fires the beat's registered tools, returning `true` to skip the real send.
 */

import { useCallback } from 'react';
import { useAgentState } from '@/context/agent-state.context';
import { isApDemoMode } from '../demo-mode';
import { getApDemoTool, matchApDemoScript } from './script';

export function useApDemoInterceptor(): {
  tryIntercept: (message: string) => boolean;
} {
  const { runDemoScriptedTurn } = useAgentState();

  const tryIntercept = useCallback(
    (message: string): boolean => {
      if (!isApDemoMode()) return false;
      const script = matchApDemoScript(message);
      if (!script) return false;

      const runTools = async () => {
        for (const toolName of script.toolNames) {
          const tool = getApDemoTool(toolName);
          if (!tool) {
            console.warn(
              `[ap-demo] scripted tool "${toolName}" not registered`,
            );
            continue;
          }
          try {
            await tool();
          } catch (err) {
            console.error(`[ap-demo] tool "${toolName}" threw`, err);
          }
        }
      };

      void runDemoScriptedTurn(message, script.narration, runTools);
      return true;
    },
    [runDemoScriptedTurn],
  );

  return { tryIntercept };
}
